import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

export type PaperSize = 'a4' | 'letter' | 'legal' | 'a3';

export const PAPER_DIMENSIONS: Record<PaperSize, { widthMm: number; heightMm: number; format: string; label: string }> = {
  a4: { widthMm: 210, heightMm: 297, format: 'a4', label: 'A4 (210 × 297 mm - Standard)' },
  letter: { widthMm: 215.9, heightMm: 279.4, format: 'letter', label: 'US Letter (8.5 × 11 in)' },
  legal: { widthMm: 215.9, heightMm: 355.6, format: 'legal', label: 'US Legal (8.5 × 14 in)' },
  a3: { widthMm: 297, heightMm: 420, format: 'a3', label: 'A3 Deluxe Poster (297 × 420 mm)' }
};

/**
 * Sanitizes and formats a report filename to prevent invalid filesystem characters.
 */
export function cleanReportFilename(rawName?: string, defaultPrefix = 'VedangJyotisham_Report'): string {
  if (!rawName || !rawName.trim()) {
    return `${defaultPrefix}_${new Date().toISOString().split('T')[0]}.pdf`;
  }
  const sanitized = rawName
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  const base = sanitized || defaultPrefix;
  return base.endsWith('.pdf') ? base : `${base}.pdf`;
}

/**
 * High-definition client-side PDF export utility
 * Uses html2canvas-pro with discrete page-by-page rendering to completely eliminate
 * horizontal seams/line cuts across charts, tables, and text elements.
 */
export async function downloadElementAsPdf(
  elementId: string,
  filename: string,
  onProgress?: (msg: string) => void,
  paperSize: PaperSize = 'a4'
): Promise<boolean> {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Report element with ID "${elementId}" not found in DOM.`);
  }

  if (onProgress) onProgress('Preparing document layout for PDF conversion...');

  const cleanFilename = cleanReportFilename(filename);
  const isEcoBw = element.classList.contains('bg-white') || element.className.includes('bg-white');
  const targetBg = isEcoBw ? '#FFFFFF' : '#FFFDF7';
  const paperConfig = PAPER_DIMENSIONS[paperSize] || PAPER_DIMENSIONS.a4;
  const pdfPageWidth = paperConfig.widthMm;
  const pdfPageHeight = paperConfig.heightMm;

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: paperConfig.format as any,
      compress: true
    });

    // Query discrete page containers (.dossier-page)
    const pageNodes = Array.from(element.querySelectorAll<HTMLElement>('.dossier-page'));

    if (pageNodes.length > 0) {
      // Discrete Page-by-Page Rendering (Zero Seams / Zero Element Cuts!)
      for (let i = 0; i < pageNodes.length; i++) {
        const pageEl = pageNodes[i];
        if (onProgress) {
          onProgress(`Rendering page ${i + 1} of ${pageNodes.length} (${Math.round(((i + 1) / pageNodes.length) * 100)}%)...`);
        }

        const pageCanvas = await html2canvas(pageEl, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: targetBg,
          windowWidth: pageEl.scrollWidth || 850
        });

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.96);

        if (i > 0) {
          pdf.addPage(paperConfig.format as any, 'portrait');
        }

        pdf.addImage(pageImgData, 'JPEG', 0, 0, pdfPageWidth, pdfPageHeight, undefined, 'FAST');
      }
    } else {
      // Fallback for non-dossier containers
      if (onProgress) onProgress('Rendering vector layout...');
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        scrollY: 0,
        scrollX: 0,
        backgroundColor: targetBg,
        windowWidth: element.scrollWidth || 850
      });

      const pageCanvasHeight = Math.floor((canvas.width * pdfPageHeight) / pdfPageWidth);
      const totalPages = Math.max(1, Math.ceil(canvas.height / pageCanvasHeight));

      let renderedHeight = 0;
      let pageNumber = 1;

      while (renderedHeight < canvas.height) {
        if (onProgress) {
          onProgress(`Processing page ${pageNumber} of ${totalPages}...`);
        }

        const currentSliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageCanvasHeight;
        const ctx = pageCanvas.getContext('2d');

        if (ctx) {
          ctx.fillStyle = targetBg;
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            renderedHeight,
            canvas.width,
            currentSliceHeight,
            0,
            0,
            canvas.width,
            currentSliceHeight
          );
        }

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.96);
        if (pageNumber > 1) {
          pdf.addPage(paperConfig.format as any, 'portrait');
        }

        pdf.addImage(pageImgData, 'JPEG', 0, 0, pdfPageWidth, pdfPageHeight, undefined, 'FAST');
        renderedHeight += pageCanvasHeight;
        pageNumber++;
      }
    }

    if (onProgress) onProgress('Finalizing and downloading PDF file...');
    pdf.save(cleanFilename);
    return true;
  } catch (err) {
    console.error('PDF export error:', err);
    throw err;
  }
}

/**
 * Standalone Printable Document Opener (Iframe-Bypass for Browser Security)
 * Opens the styled report in a clean new window with auto-print & manual print bar
 */
export function openPrintableDocumentInNewTab(
  elementId: string,
  documentTitle: string,
  paperSize: PaperSize = 'a4'
): boolean {
  const sourceEl = document.getElementById(elementId);
  if (!sourceEl) {
    window.print();
    return false;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    window.print();
    return false;
  }

  const isEcoBw = sourceEl.classList.contains('bg-white') || sourceEl.className.includes('bg-white');
  const winBg = isEcoBw ? '#FFFFFF' : '#FFFDF7';
  const winColor = isEcoBw ? '#000000' : '#263332';
  const styleTags = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map(el => el.outerHTML)
    .join('\n');

  const paperConfig = PAPER_DIMENSIONS[paperSize] || PAPER_DIMENSIONS.a4;
  const paperCssSize = paperSize === 'letter' ? 'letter' : paperSize === 'legal' ? 'legal' : paperSize === 'a3' ? 'A3' : 'A4';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${documentTitle.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Cinzel:wght@600;700&family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap" rel="stylesheet">
  ${styleTags}
  <style>
    body {
      background-color: ${winBg} !important;
      color: ${winColor} !important;
      margin: 0;
      padding: 16px;
      display: flex;
      justify-content: center;
      font-family: 'Plus Jakarta Sans', 'Noto Sans Devanagari', system-ui, -apple-system, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    @page {
      size: ${paperCssSize} portrait;
      margin: 8mm 10mm 8mm 10mm;
    }
    @media print {
      body {
        padding: 0 !important;
      }
      .no-print-floating-bar {
        display: none !important;
      }
    }
    .no-print-floating-bar {
      position: sticky;
      top: 12px;
      z-index: 9999;
      background: #174C4B;
      color: #FFFDF7;
      padding: 10px 18px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      margin-bottom: 20px;
      box-shadow: 0 10px 25px -5px rgba(18, 59, 58, 0.35);
      border: 1px solid #B58A4A;
    }
    .no-print-btn {
      background: #B58A4A;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 13px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: background 0.2s ease;
    }
    .no-print-btn:hover {
      background: #967035;
    }
  </style>
</head>
<body>
  <div style="width: 100%; max-width: 850px;">
    <div class="no-print-floating-bar">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 16px;">🪔</span>
        <span style="font-weight: 700; font-size: 14px; letter-spacing: 0.02em;">${documentTitle.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>
      </div>
      <div style="display: flex; align-items: center; gap: 10px;">
        <button class="no-print-btn" onclick="window.print()">
          <span>🖨️ Save as PDF / Print</span>
        </button>
      </div>
    </div>
    ${sourceEl.outerHTML}
  </div>
  <script>
    window.addEventListener('load', function() {
      // Auto-trigger print prompt after rendering fonts & SVGs
      setTimeout(function() {
        window.print();
      }, 600);
    });
  </script>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}
